import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import {
  getAllUsersApi,
  createUserApi,
  updateUserStatusApi,
  deleteUserApi,
  getAllNINRecords,
  getStoredUser,
} from "../../services/Request";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  FaUserPlus,
  FaUsers,
  FaIdCard,
  FaCheckCircle,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaUserShield,
} from "react-icons/fa";

export default function UserManagement() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [ninRecords, setNinRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users"); // 'users' or 'ninRecords'

  // New User Form State
  const [showAddModal, setShowAddModal] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [creating, setCreating] = useState(false);

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, ninRes] = await Promise.allSettled([
        getAllUsersApi(),
        getAllNINRecords(),
      ]);

      if (usersRes.status === "fulfilled" && usersRes.value?.users) {
        setUsers(usersRes.value.users);
      }

      if (ninRes.status === "fulfilled" && ninRes.value?.records) {
        setNinRecords(ninRes.value.records);
      }
    } catch (err) {
      toast.error(err.message || "Failed to load management data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const user = getStoredUser();
    if (!user) {
      navigate("/login");
      return;
    }
    if (user.role !== "admin") {
      toast.error("Access restricted: Administrator rights required.");
      navigate("/");
      return;
    }
    setCurrentUser(user);
    fetchAllData();
  }, [navigate, fetchAllData]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.warning("Please provide name, email, and password.");
      return;
    }

    try {
      setCreating(true);
      const res = await createUserApi({
        name: name.trim(),
        email: email.trim(),
        password,
        role,
      });

      if (res.status) {
        toast.success(`User ${name} created successfully!`);
        setName("");
        setEmail("");
        setPassword("");
        setRole("user");
        setShowAddModal(false);
        fetchAllData();
      } else {
        toast.error(res.message || "Failed to create user.");
      }
    } catch (err) {
      toast.error(err.message || "User creation failed.");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 1 ? 0 : 1;
    try {
      const res = await updateUserStatusApi(user.id, newStatus === 1);
      if (res.status) {
        toast.success(`User status updated to ${newStatus === 1 ? "Active" : "Inactive"}`);
        setUsers((prev) =>
          prev.map((u) => (u.id === user.id ? { ...u, status: newStatus } : u))
        );
      }
    } catch (err) {
      toast.error(err.message || "Failed to update status.");
    }
  };

  const handleDeleteUser = async (user) => {
    if (user.id === currentUser?.id) {
      toast.error("You cannot delete your own active administrator account.");
      return;
    }

    const confirm = await Swal.fire({
      title: `Delete user "${user.name}"?`,
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#DC2626",
      cancelButtonColor: "#64748B",
      confirmButtonText: "Yes, delete",
    });

    if (confirm.isConfirmed) {
      try {
        const res = await deleteUserApi(user.id);
        if (res.status) {
          toast.success("User deleted successfully.");
          setUsers((prev) => prev.filter((u) => u.id !== user.id));
        }
      } catch (err) {
        toast.error(err.message || "Failed to delete user.");
      }
    }
  };

  const activeCount = users.filter((u) => u.status === 1).length;
  const adminCount = users.filter((u) => u.role === "admin").length;

  return (
    <div style={styles.page}>
      <Navbar />

      <main style={styles.main}>
        {/* Page Top Header */}
        <div style={styles.topHeader}>
          <div>
            <h1 style={styles.pageTitle}>User & Registry Management</h1>
            <p style={styles.pageSubtitle}>
              Manage administrative staff, field verification officers, and inspection archives
            </p>
          </div>

          <button
            onClick={() => setShowAddModal(true)}
            style={styles.addUserBtn}
          >
            <FaUserPlus style={{ marginRight: "8px" }} />
            Create New User
          </button>
        </div>

        {/* Stats Grid */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIconBox, backgroundColor: "#ECFDF5", color: "#064E3B" }}>
              <FaUsers />
            </div>
            <div>
              <div style={styles.statNum}>{users.length}</div>
              <div style={styles.statLabel}>Total Registered Users</div>
            </div>
          </div>

          <div style={styles.statCard}>
            <div style={{ ...styles.statIconBox, backgroundColor: "#EFF6FF", color: "#2563EB" }}>
              <FaCheckCircle />
            </div>
            <div>
              <div style={styles.statNum}>{activeCount}</div>
              <div style={styles.statLabel}>Active Officers</div>
            </div>
          </div>

          <div style={styles.statCard}>
            <div style={{ ...styles.statIconBox, backgroundColor: "#FEF3C7", color: "#D97706" }}>
              <FaUserShield />
            </div>
            <div>
              <div style={styles.statNum}>{adminCount}</div>
              <div style={styles.statLabel}>System Administrators</div>
            </div>
          </div>

          <div style={styles.statCard}>
            <div style={{ ...styles.statIconBox, backgroundColor: "#F3E8FF", color: "#7E22CE" }}>
              <FaIdCard />
            </div>
            <div>
              <div style={styles.statNum}>{ninRecords.length}</div>
              <div style={styles.statLabel}>NIN Archive Records</div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div style={styles.tabsContainer}>
          <button
            onClick={() => setActiveTab("users")}
            style={{
              ...styles.tabBtn,
              ...(activeTab === "users" ? styles.activeTabBtn : {}),
            }}
          >
            <FaUsers style={{ marginRight: "6px" }} />
            System Users Directory ({users.length})
          </button>
          <button
            onClick={() => setActiveTab("ninRecords")}
            style={{
              ...styles.tabBtn,
              ...(activeTab === "ninRecords" ? styles.activeTabBtn : {}),
            }}
          >
            <FaIdCard style={{ marginRight: "6px" }} />
            Verified NIN Registry ({ninRecords.length})
          </button>
        </div>

        {/* Table Content */}
        {loading ? (
          <div style={styles.loadingBox}>
            <div style={styles.spinner}></div>
            <p>Loading registry information...</p>
          </div>
        ) : activeTab === "users" ? (
          /* Users Table */
          <div style={styles.tableCard} className="fade-in">
            <table style={styles.table}>
              <thead>
                <tr style={styles.thRow}>
                  <th style={styles.th}>USER</th>
                  <th style={styles.th}>EMAIL</th>
                  <th style={styles.th}>ROLE</th>
                  <th style={styles.th}>STATUS</th>
                  <th style={styles.th}>NIN VERIFIED</th>
                  <th style={styles.th}>CREATED AT</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={styles.emptyTd}>
                      No users found.
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id} style={styles.tr}>
                      <td style={styles.td}>
                        <div style={styles.userCell}>
                          <div style={styles.tableAvatar}>
                            {u.name ? u.name.charAt(0).toUpperCase() : "U"}
                          </div>
                          <span style={styles.tableUserName}>{u.name}</span>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.tableEmail}>{u.email}</span>
                      </td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.roleBadge,
                            backgroundColor:
                              u.role === "admin" ? "#FEF3C7" : "#E0F2FE",
                            color: u.role === "admin" ? "#92400E" : "#0369A1",
                          }}
                        >
                          {u.role === "admin" ? "ADMIN" : "OFFICER"}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.statusBadge,
                            backgroundColor:
                              u.status === 1 ? "#D1FAE5" : "#FEE2E2",
                            color: u.status === 1 ? "#065F46" : "#991B1B",
                          }}
                        >
                          {u.status === 1 ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {u.ninVerifiedAt ? (
                          <span style={styles.verifiedDate}>
                            {new Date(u.ninVerifiedAt).toLocaleDateString()}
                          </span>
                        ) : (
                          <span style={styles.unverifiedLabel}>Pending</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        <span style={styles.dateLabel}>
                          {new Date(u.createdAt).toLocaleDateString()}
                        </span>
                      </td>
                      <td style={{ ...styles.td, textAlign: "right" }}>
                        <div style={styles.actionsGroup}>
                          <button
                            onClick={() => handleToggleStatus(u)}
                            style={styles.iconBtn}
                            title={u.status === 1 ? "Deactivate User" : "Activate User"}
                          >
                            {u.status === 1 ? (
                              <FaToggleOn style={{ color: "#059669", fontSize: "20px" }} />
                            ) : (
                              <FaToggleOff style={{ color: "#94A3B8", fontSize: "20px" }} />
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteUser(u)}
                            style={{ ...styles.iconBtn, color: "#DC2626" }}
                            title="Delete User"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          /* NIN Records Table */
          <div style={styles.tableCard} className="fade-in">
            <table style={styles.table}>
              <thead>
                <tr style={styles.thRow}>
                  <th style={styles.th}>NIN</th>
                  <th style={styles.th}>CITIZEN NAME</th>
                  <th style={styles.th}>PHONE</th>
                  <th style={styles.th}>GENDER</th>
                  <th style={styles.th}>SOURCE</th>
                  <th style={styles.th}>VERIFIED AT</th>
                  <th style={styles.th}>OPERATOR</th>
                </tr>
              </thead>
              <tbody>
                {ninRecords.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={styles.emptyTd}>
                      No verified NIN records stored yet.
                    </td>
                  </tr>
                ) : (
                  ninRecords.map((r) => (
                    <tr key={r.id} style={styles.tr}>
                      <td style={styles.td}>
                        <span style={styles.ninCell}>{r.nin}</span>
                      </td>
                      <td style={styles.td}>
                        <strong>{r.firstname} {r.middlename ? r.middlename + " " : ""}{r.surname}</strong>
                      </td>
                      <td style={styles.td}>{r.phone || "—"}</td>
                      <td style={styles.td}>{r.gender || "—"}</td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.roleBadge,
                            backgroundColor:
                              r.source === "database" ? "#EFF6FF" : "#ECFDF5",
                            color: r.source === "database" ? "#1D4ED8" : "#047857",
                          }}
                        >
                          {r.source ? r.source.toUpperCase() : "QOREID"}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {new Date(r.verifiedAt || r.createdAt).toLocaleString()}
                      </td>
                      <td style={styles.td}>
                        {r.user ? r.user.name || r.user.email : "Direct Client"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal: Create New User */}
        {showAddModal && (
          <div style={styles.modalOverlay} className="fade-in">
            <div style={styles.modalBox}>
              <div style={styles.modalHeader}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <FaUserPlus style={{ color: "#064E3B", fontSize: "20px" }} />
                  <h2 style={styles.modalTitle}>Create New System User</h2>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  style={styles.closeModalBtn}
                >
                  &times;
                </button>
              </div>

              <form onSubmit={handleCreateUser} style={styles.modalForm}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Full Legal Name</label>
                  <input
                    type="text"
                    required
                    style={styles.modalInput}
                    placeholder="e.g. John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Official Email Address</label>
                  <input
                    type="email"
                    required
                    style={styles.modalInput}
                    placeholder="e.g. j.doe@nin.gov.ng"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Initial Password</label>
                  <input
                    type="password"
                    required
                    style={styles.modalInput}
                    placeholder="Minimum 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>System Role</label>
                  <select
                    style={styles.modalInput}
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                  >
                    <option value="user">Verification Officer (Standard User)</option>
                    <option value="admin">System Administrator (Full Privileges)</option>
                  </select>
                </div>

                <div style={styles.modalActions}>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    style={styles.cancelBtn}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    style={{
                      ...styles.saveUserBtn,
                      opacity: creating ? 0.75 : 1,
                    }}
                  >
                    {creating ? "Creating..." : "Save User"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#F8FAFC",
  },
  main: {
    maxWidth: "1280px",
    margin: "32px auto",
    padding: "0 24px",
  },
  topHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "28px",
  },
  pageTitle: {
    fontSize: "24px",
    fontWeight: "800",
    color: "#0F172A",
    letterSpacing: "-0.5px",
    margin: "0 0 4px 0",
  },
  pageSubtitle: {
    fontSize: "14px",
    color: "#64748B",
    margin: 0,
  },
  addUserBtn: {
    display: "flex",
    alignItems: "center",
    padding: "12px 20px",
    backgroundColor: "#064E3B",
    color: "#FFFFFF",
    fontSize: "14px",
    fontWeight: "700",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    boxShadow: "0 4px 6px -1px rgba(6, 78, 59, 0.2)",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "20px",
    marginBottom: "32px",
  },
  statCard: {
    backgroundColor: "#FFFFFF",
    padding: "20px",
    borderRadius: "16px",
    border: "1px solid #E2E8F0",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
  },
  statIconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
  },
  statNum: {
    fontSize: "24px",
    fontWeight: "800",
    color: "#0F172A",
  },
  statLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#64748B",
  },
  tabsContainer: {
    display: "flex",
    gap: "12px",
    marginBottom: "20px",
  },
  tabBtn: {
    display: "flex",
    alignItems: "center",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "700",
    color: "#64748B",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  activeTabBtn: {
    backgroundColor: "#064E3B",
    color: "#FFFFFF",
    borderColor: "#064E3B",
  },
  tableCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    border: "1px solid #E2E8F0",
    overflow: "hidden",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "left",
  },
  thRow: {
    backgroundColor: "#F8FAFC",
    borderBottom: "1px solid #E2E8F0",
  },
  th: {
    padding: "14px 20px",
    fontSize: "11px",
    fontWeight: "800",
    color: "#64748B",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
  tr: {
    borderBottom: "1px solid #F1F5F9",
    transition: "background-color 0.15s",
  },
  td: {
    padding: "16px 20px",
    fontSize: "13px",
    color: "#334155",
  },
  userCell: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  tableAvatar: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: "#064E3B",
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: "700",
  },
  tableUserName: {
    fontWeight: "700",
    color: "#0F172A",
  },
  tableEmail: {
    color: "#64748B",
  },
  roleBadge: {
    padding: "3px 8px",
    borderRadius: "6px",
    fontSize: "10px",
    fontWeight: "800",
    letterSpacing: "0.5px",
  },
  statusBadge: {
    padding: "3px 8px",
    borderRadius: "6px",
    fontSize: "10px",
    fontWeight: "800",
    letterSpacing: "0.5px",
  },
  verifiedDate: {
    color: "#059669",
    fontWeight: "600",
  },
  unverifiedLabel: {
    color: "#94A3B8",
  },
  dateLabel: {
    color: "#64748B",
  },
  actionsGroup: {
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  iconBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
  },
  emptyTd: {
    padding: "40px",
    textAlign: "center",
    color: "#94A3B8",
    fontSize: "14px",
  },
  ninCell: {
    fontWeight: "800",
    color: "#064E3B",
    letterSpacing: "0.5px",
  },
  loadingBox: {
    textAlign: "center",
    padding: "60px 0",
    color: "#64748B",
  },
  spinner: {
    width: "36px",
    height: "36px",
    margin: "0 auto 16px",
    border: "3px solid #E2E8F0",
    borderTopColor: "#064E3B",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(15, 23, 42, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    padding: "20px",
  },
  modalBox: {
    backgroundColor: "#FFFFFF",
    borderRadius: "18px",
    width: "100%",
    maxWidth: "480px",
    padding: "28px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
    paddingBottom: "14px",
    borderBottom: "1px solid #F1F5F9",
  },
  modalTitle: {
    fontSize: "18px",
    fontWeight: "800",
    color: "#0F172A",
    margin: 0,
  },
  closeModalBtn: {
    background: "none",
    border: "none",
    fontSize: "24px",
    color: "#94A3B8",
    cursor: "pointer",
  },
  modalForm: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  formLabel: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#334155",
  },
  modalInput: {
    padding: "11px 14px",
    fontSize: "14px",
    border: "1px solid #CBD5E1",
    borderRadius: "8px",
    outline: "none",
    backgroundColor: "#F8FAFC",
  },
  modalActions: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    marginTop: "12px",
  },
  cancelBtn: {
    padding: "10px 18px",
    fontSize: "13px",
    fontWeight: "700",
    color: "#64748B",
    backgroundColor: "#F1F5F9",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
  saveUserBtn: {
    padding: "10px 20px",
    fontSize: "13px",
    fontWeight: "700",
    color: "#FFFFFF",
    backgroundColor: "#064E3B",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
};
